import { useState, useRef } from "react";
import { useApp } from "@/contexts/AppContext";
import api, { formatCurrency } from "@/lib/api";
import { Upload, Download, FileText, CheckCircle, AlertTriangle, Table } from "lucide-react";

const SAMPLE_CSV = `Entity/Division,Asset Tag,Description,Category,Acquired Date,Original Cost,Accumulated Depreciation,Current Value,Useful Life (months),Residual Value,Depreciation Method,Location,Custodian
ATI,LB-0001,Head Office Building,Land and Buildings,2020-01-15,5000000,500000,,600,500000,STRAIGHT_LINE,Head Office,J. Smith
ATI,FF-0001,Office Furniture Set,Furniture and Fixtures,2021-06-01,250000,120000,,120,12500,STRAIGHT_LINE,Head Office,A. Naidoo
EUP,MV-0001,Delivery Van,Motor Vehicles,2022-03-10,450000,180000,,60,67500,STRAIGHT_LINE,Warehouse,T. Pillay`;

export default function ImportPage() {
  const { loadEntities, loadCategories } = useApp();
  const [csvText, setCsvText] = useState("");
  const [asAtDate, setAsAtDate] = useState(new Date().toISOString().substring(0, 10));
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [preview, setPreview] = useState(null);
  const fileRef = useRef(null);

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target.result;
      setCsvText(text);
      parsePreview(text);
    };
    reader.readAsText(file);
  };

  const parsePreview = (text) => {
    const lines = text.trim().split("\n");
    if (lines.length < 2) return;
    const headers = lines[0].split(",").map(h => h.trim());
    const rows = lines.slice(1).map(line => {
      const vals = line.split(",").map(v => v.trim());
      const row = {};
      headers.forEach((h, i) => row[h] = vals[i] || "");
      return row;
    });
    setPreview({ headers, rows, count: rows.length });
  };

  const handlePaste = (text) => {
    setCsvText(text);
    if (text.trim()) parsePreview(text);
  };

  const loadSample = () => {
    setCsvText(SAMPLE_CSV);
    parsePreview(SAMPLE_CSV);
  };

  const downloadTemplate = async () => {
    try {
      const { data } = await api.get("/import/template", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = "stratum_import_template.csv";
      link.click();
    } catch (e) {
      alert("Error downloading template");
    }
  };

  const doImport = async () => {
    if (!csvText.trim()) return;
    setImporting(true);
    setResult(null);
    try {
      const { data } = await api.post("/import/csv", { csv_data: csvText, as_at_date: asAtDate });
      setResult(data);
      // Refresh entities and categories
      await loadEntities();
      await loadCategories();
    } catch (e) {
      setResult({ error: e.response?.data?.detail || "Import failed" });
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="import-page">
      <div>
        <h1 className="font-display text-2xl font-semibold text-[#0B1020]">
          <Upload size={24} className="inline mr-2 text-[#C8A961]" />
          Import Asset Register
        </h1>
        <p className="text-sm text-[#6B7280] mt-1">
          Import your existing Fixed Asset Register from CSV. Entities will be auto-created from the Entity/Division column.
        </p>
      </div>

      {/* Instructions */}
      <div className="stratum-card p-6">
        <h3 className="font-display text-lg font-semibold mb-3">How it works</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
          <div className="p-4 rounded-lg bg-[#FAF8F4] border border-[#E8E2D5]">
            <div className="text-[#C8A961] font-mono text-lg mb-2">1</div>
            <p className="font-medium text-[#0B1020] mb-1">Prepare your CSV</p>
            <p className="text-[#6B7280] text-xs">Include columns for Entity/Division, Asset Tag, Description, Category, Cost, Depreciation, Dates, and Policy info.</p>
          </div>
          <div className="p-4 rounded-lg bg-[#FAF8F4] border border-[#E8E2D5]">
            <div className="text-[#C8A961] font-mono text-lg mb-2">2</div>
            <p className="font-medium text-[#0B1020] mb-1">Upload or paste</p>
            <p className="text-[#6B7280] text-xs">Upload a .csv file or paste directly. Stratum auto-detects column names using smart matching.</p>
          </div>
          <div className="p-4 rounded-lg bg-[#FAF8F4] border border-[#E8E2D5]">
            <div className="text-[#C8A961] font-mono text-lg mb-2">3</div>
            <p className="font-medium text-[#0B1020] mb-1">Auto-creates entities</p>
            <p className="text-[#6B7280] text-xs">Each unique Entity/Division value becomes a company in your group. Categories are matched to IFRS standards.</p>
          </div>
          <div className="p-4 rounded-lg bg-[#FAF8F4] border border-[#E8E2D5]">
            <div className="text-[#C8A961] font-mono text-lg mb-2">4</div>
            <p className="font-medium text-[#0B1020] mb-1">Ready to go</p>
            <p className="text-[#6B7280] text-xs">Assets are imported with correct carrying amounts. Depreciation engine calculates from your policy settings.</p>
          </div>
        </div>
      </div>

      {/* Upload Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div className="stratum-card p-6">
            <h3 className="font-display text-base font-semibold mb-3">Upload CSV File</h3>
            <div className="border-2 border-dashed border-[#E8E2D5] rounded-lg p-8 text-center hover:border-[#C8A961] transition-colors cursor-pointer"
              onClick={() => fileRef.current?.click()}>
              <Upload size={32} className="mx-auto text-[#C8A961] mb-3" />
              <p className="text-sm text-[#1A1A1A] font-medium">Click to upload or drag a CSV file</p>
              <p className="text-xs text-[#6B7280] mt-1">Supports .csv files from Excel, Sage, Pastel, Xero, etc.</p>
              <input ref={fileRef} type="file" accept=".csv,.txt" className="hidden" onChange={handleFileUpload} data-testid="csv-file-input" />
            </div>
            
            <div className="flex gap-3 mt-4">
              <button onClick={downloadTemplate} className="px-4 py-2 rounded-lg border border-[#E8E2D5] text-sm hover:bg-[#FAF8F4] flex items-center gap-2" data-testid="download-template-btn">
                <Download size={14} /> Download Template
              </button>
              <button onClick={loadSample} className="px-4 py-2 rounded-lg border border-[#E8E2D5] text-sm hover:bg-[#FAF8F4] flex items-center gap-2" data-testid="load-sample-btn">
                <Table size={14} /> Load Sample Data
              </button>
            </div>
          </div>

          <div className="stratum-card p-6">
            <h3 className="font-display text-base font-semibold mb-3">Or Paste CSV Data</h3>
            <textarea
              className="stratum-input w-full font-mono text-xs"
              rows={10}
              value={csvText}
              onChange={(e) => handlePaste(e.target.value)}
              placeholder="Paste your CSV data here..."
              data-testid="csv-textarea"
            />
          </div>

          <div className="stratum-card p-6">
            <div className="flex items-center gap-4">
              <div>
                <label className="block text-xs font-medium text-[#6B7280] mb-1">As-at Date (opening balance date)</label>
                <input type="date" className="stratum-input" value={asAtDate} onChange={(e) => setAsAtDate(e.target.value)} data-testid="as-at-date-input" />
              </div>
              <button
                onClick={doImport}
                disabled={importing || !csvText.trim()}
                className="stratum-btn-primary px-6 py-3 mt-4 disabled:opacity-50 flex items-center gap-2"
                data-testid="import-btn"
              >
                <Upload size={16} /> {importing ? "Importing..." : "Import Assets"}
              </button>
            </div>
          </div>
        </div>

        {/* Preview & Results */}
        <div className="space-y-4">
          {preview && (
            <div className="stratum-card p-5">
              <h3 className="font-display text-base font-semibold mb-3">
                <FileText size={16} className="inline mr-2 text-[#C8A961]" />
                Preview ({preview.count} rows)
              </h3>
              <div className="overflow-x-auto max-h-96">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-[#0B1020] text-white">
                      {preview.headers.slice(0, 7).map((h, i) => (
                        <th key={i} className="px-2 py-1.5 text-left font-medium whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.rows.slice(0, 15).map((row, i) => (
                      <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-[#FAF8F4]"}>
                        {preview.headers.slice(0, 7).map((h, j) => (
                          <td key={j} className="px-2 py-1 whitespace-nowrap max-w-[120px] truncate">{row[h]}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {preview.count > 15 && <p className="text-xs text-[#6B7280] mt-2">...and {preview.count - 15} more rows</p>}
              </div>
            </div>
          )}

          {result && (
            <div className={`stratum-card p-5 ${result.error ? "border-l-4 border-l-[#991B1B]" : "border-l-4 border-l-[#065F46]"}`}>
              {result.error ? (
                <div className="flex items-center gap-2 text-[#991B1B]">
                  <AlertTriangle size={18} />
                  <span className="font-medium">{result.error}</span>
                </div>
              ) : (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <CheckCircle size={18} className="text-[#065F46]" />
                    <span className="font-medium text-[#065F46]">Import Successful!</span>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-[#6B7280]">Assets imported:</span>
                      <span className="font-mono tabular-nums ml-2 font-semibold">{result.imported}</span>
                    </div>
                    <div>
                      <span className="text-[#6B7280]">Total cost:</span>
                      <span className="font-mono tabular-nums ml-2 font-semibold">{formatCurrency(result.total_cost)}</span>
                    </div>
                    <div>
                      <span className="text-[#6B7280]">Total carrying:</span>
                      <span className="font-mono tabular-nums ml-2 font-semibold">{formatCurrency(result.total_carrying)}</span>
                    </div>
                    <div>
                      <span className="text-[#6B7280]">As-at date:</span>
                      <span className="font-mono tabular-nums ml-2">{result.as_at_date}</span>
                    </div>
                    {result.entities_created?.length > 0 && (
                      <div className="col-span-2">
                        <span className="text-[#6B7280]">Entities created:</span>
                        <span className="ml-2 font-medium">{result.entities_created.join(", ")}</span>
                      </div>
                    )}
                  </div>
                  {result.errors?.length > 0 && (
                    <div className="mt-3 p-3 rounded-lg bg-[#92400E]/10">
                      <span className="text-xs font-medium text-[#92400E]">Warnings ({result.errors.length}):</span>
                      {result.errors.map((err, i) => (
                        <p key={i} className="text-xs text-[#92400E] mt-1">{err}</p>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Column Mapping Reference */}
          <div className="stratum-card p-5">
            <h3 className="font-display text-base font-semibold mb-3">Supported Column Names</h3>
            <div className="text-xs text-[#6B7280] space-y-1.5">
              <p><strong className="text-[#1A1A1A]">Entity/Division:</strong> Entity, Division, Company, Subsidiary</p>
              <p><strong className="text-[#1A1A1A]">Asset Tag:</strong> Tag, Asset Tag, Asset Number, Asset No</p>
              <p><strong className="text-[#1A1A1A]">Description:</strong> Description, Desc, Asset Description</p>
              <p><strong className="text-[#1A1A1A]">Category:</strong> Category, Class, Type, Asset Category</p>
              <p><strong className="text-[#1A1A1A]">Acquired Date:</strong> Acquired, Purchase Date, Date Acquired</p>
              <p><strong className="text-[#1A1A1A]">Original Cost:</strong> Cost, Original Cost, Purchase Price</p>
              <p><strong className="text-[#1A1A1A]">Accumulated Depreciation:</strong> Accum Dep, Accumulated Depreciation</p>
              <p><strong className="text-[#1A1A1A]">Current Value:</strong> Current Value, Carrying Amount, Book Value, Net</p>
              <p><strong className="text-[#1A1A1A]">Useful Life:</strong> Useful Life, Life (months or years auto-detected)</p>
              <p><strong className="text-[#1A1A1A]">Residual Value:</strong> Residual, Residual Value, Salvage</p>
              <p><strong className="text-[#1A1A1A]">Method:</strong> Method, Depreciation Method</p>
              <p><strong className="text-[#1A1A1A]">Location:</strong> Location, Site, Branch</p>
              <p><strong className="text-[#1A1A1A]">Custodian:</strong> Custodian, Responsible Person</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
